import styles from './index.module.less'

import { useState, useEffect, ReactNode, useCallback, useLayoutEffect, useMemo } from 'react'

import { Layout, Menu, Breadcrumb, Message, Pagination, List, Tag, Space } from '@arco-design/web-react'
import { Props, useBreadcrumbs } from '@/mobile-pages/sitemap/products'

import useLocale from '@/utils/useLocale'
import Link from 'next/link'
import { useRouter } from 'next/router'

import type { ProductsSitemapRes } from '@/types/api'
import { SUCCESS_CODE, BLUR_CODE, UNAUTHORIZED_CODE } from '@/utils/constants'
import { getProductSitemapApi } from '@/api/product'
import useRegionOptions from '@/hooks/useRegionOptions'

import { useSetState, useUpdateEffect } from 'ahooks'
import { useSelector } from 'react-redux'
import { StoreState } from '@/store'
import cls from 'classnames'

const MenuItem = Menu.Item
const Sider = Layout.Sider
const Header = Layout.Header
const Footer = Layout.Footer
const Content = Layout.Content

interface RegionListProp {
  onClick: (option: any) => void
  currentCategoryNameSlug?: string
  renderItem?: ReactNode
  currentRegionObj?: ReginItemProp
  categoryKey?: string
}

export const DYNAMIC_PRODUCT_CATEGORY = `best-selling-{category}-products-in-{regionName}-on-tiktok`
const REGEX = /best-selling-(.+?)-products-in-(.+?)-on-tiktok/

export const getCategoryString = (currentCategoryNameSlug = '', currentRegion = '') => {
  return DYNAMIC_PRODUCT_CATEGORY.replace('{category}', currentCategoryNameSlug || '').replace(
    '{regionName}',
    currentRegion
  )
}

export const getUrlField = (
  str: string
): { urlCategoryNameSlug: string; urlRegion: string } | Record<string, unknown> => {
  const match = str.match(REGEX)

  if (match) {
    const urlCategoryNameSlug = match[1]
    const urlRegion = match[2]

    return {
      urlCategoryNameSlug: urlCategoryNameSlug,
      urlRegion: urlRegion
    }
  } else {
    return {}
  }
}

export const RegionList = ({
  onClick,
  currentCategoryNameSlug,
  renderItem,
  currentRegionObj,
  categoryKey = 'products_category'
}: RegionListProp) => {
  const router = useRouter()
  const t = useLocale()
  const { regionOptions } = useRegionOptions()

  return (
    <div className={styles.regionWrap}>
      <div className={styles.regionLabel}>{t['common.region']}</div>
      <div className={styles.regionList}>
        {regionOptions.map((option) => {
          return (
            <div
              className={cls(styles.regionItem, {
                [styles.selectedRegion]: currentRegionObj?.region === option.region
              })}
              key={option.region}
              onClick={() => onClick(option)}
            >
              {renderItem ? (
                renderItem
              ) : (
                <Link
                  href={{
                    pathname: router.pathname,
                    query: {
                      [categoryKey]: getCategoryString(currentCategoryNameSlug, option.nameSlug),
                      page: '1'
                    }
                  }}
                >
                  <a style={{ pointerEvents: 'none' }}>{option.name}</a>
                </Link>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export interface ReginItemProp {
  isInternalTesting?: boolean
  lang?: string
  label?: string
  sign?: string
  region: any
  name: any
  value: any
  nameSlug: string
}

type ProductItemProp = ProductsSitemapRes[number]

export const ProductItem = ({ item }: { item: ProductItemProp }) => {
  const t = useLocale()

  return (
    <div className={styles.listItem}>
      <Link href={`/products/${item.product_id}`}>
        <a>
          <div className={styles.mb6} style={{ fontWeight: 700, color: '#403d5a', wordBreak: 'break-all' }}>
            {item.product_name}
          </div>
        </a>
      </Link>
      <div className={styles.mb6}>
        <span>
          {t['seo.sitemap.shop']}:{' '}
          <Link href={`/shops/${item.seller_id}`}>
            <a>{item.seller_name}</a>
          </Link>
        </span>
        <span className={styles.ml10}>
          {t['common.region']}: {item.region.name}
        </span>
      </div>
      <div>
        <Tag color="#EBECFF" defaultChecked style={{ color: '#655AEC' }} size="small">
          {item.category}
        </Tag>
      </div>
    </div>
  )
}

export const useFooterLinks = ({ category, region }: { category: string; region: string }) => {
  const t = useLocale()
  const curCategory = category.toLowerCase() === 'all' ? '' : category

  return [
    {
      name: t['seo.sitemap.products.tag1'].replace('{category}', curCategory || '').replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.products.tag2'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.products.tag3'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.products.tag4'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.products.tag5'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.products.tag6'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    }
  ]
}

export const FooterLinks = ({
  category,
  region,
  useFooterLinks
}: {
  category: string
  region: string
  useFooterLinks: ({ category, region }: { category: string; region: string }) => { name: string; href: string }[]
}) => {
  const list = useFooterLinks({
    category,
    region
  })

  return (
    <div className={`${styles.listWrap}`} style={{ paddingBottom: '12px' }}>
      <Space size="medium" wrap>
        {list.map((item) => {
          return (
            <Tag key={item.name}>
              <Link href={item.href}>
                <a>{item.name}</a>
              </Link>
            </Tag>
          )
        })}
      </Space>
    </div>
  )
}

const ProductSitemap = ({ filtersFromSSR, dataFromSSR }: Props) => {
  const { product_categories } = filtersFromSSR || {}
  const t = useLocale()
  const [activeCategoryKey, setActiveCategoryKey] = useState(product_categories[0]?.key || 'All')
  const [dataList, setDataList] = useState<ProductsSitemapRes>(dataFromSSR?.data || [])
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const { region } = useSelector((state: StoreState) => state.ssr)
  const { regionOptions } = useRegionOptions()
  const [pageObj, setPageObj] = useSetState({
    total: dataFromSSR.meta?.total || 0,
    pageSize: dataFromSSR.meta?.per_page || 200
  })
  const originRegionObj = regionOptions.find((r) => r.region === region)
  const currentUrlCategory = router.query.products_category
  const currentUrlPage = router.query.page || '1'
  const currentCategoryObj = product_categories.find((c) => c.key === activeCategoryKey)
  const currentCategoryNameSlug = currentCategoryObj?.name_slug
  const [currentRegionObj, setCurrentRegionObj] = useState<ReginItemProp | undefined>(originRegionObj)
  const breadcrumbs = useBreadcrumbs({
    currentCategoryObj,
    currentRegionObj
  })
  const { urlCategoryNameSlug, urlRegion } = getUrlField(currentUrlCategory as string)

  // console.log('当前id', activeCategoryKey, currentUrlCategory, currentCategoryNameSlug, regionOptions)

  useLayoutEffect(() => {
    if (urlCategoryNameSlug && urlRegion) {
      const curActiveCategoryKey = product_categories.find((c) => c.name_slug === urlCategoryNameSlug)?.key
      curActiveCategoryKey && setActiveCategoryKey(curActiveCategoryKey)
      const curReginObj = regionOptions.find((r) => r.nameSlug === urlRegion)
      curReginObj && setCurrentRegionObj(curReginObj)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchList = useCallback(
    async (params: { category_id: string; page: string; region: string }) => {
      setLoading(true)
      getProductSitemapApi({ ...params, category_id: params.category_id === '-1' ? '' : params.category_id })
        .then((res) => {
          if (res.data.code === SUCCESS_CODE) {
            setDataList(res.data.data || [])
            setPageObj({
              total: res.data.meta?.total,
              pageSize: res.data.meta?.per_page
            })
          }
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [setPageObj]
  )

  useUpdateEffect(() => {
    fetchList({
      category_id: product_categories.find((c) => c.key === activeCategoryKey)?.id || '',
      page: currentUrlPage as string,
      region: currentRegionObj?.region || ''
    })
  }, [activeCategoryKey, currentRegionObj?.region, currentUrlPage, fetchList, product_categories])

  const handleReginClick = (item: ReginItemProp) => {
    setCurrentRegionObj(item)

    router.replace(
      {
        pathname: router.basePath,
        query: {
          products_category: getCategoryString(currentCategoryNameSlug, item.nameSlug),
          page: '1'
        }
      },
      undefined,
      { scroll: false, shallow: true }
    )
  }

  return (
    <div className={styles.productsSitemap}>
      <Layout className="layout-collapse-demo">
        <Sider breakpoint="xl">
          <div className="logo" />
          <Menu
            selectedKeys={[activeCategoryKey]}
            onClickMenuItem={(key) => {
              setActiveCategoryKey(key)
            }}
            style={{ width: '100%' }}
            className={styles.menuWrap}
          >
            {product_categories.map((item) => {
              return (
                <MenuItem key={item.key}>
                  {/* <IconHome /> */}
                  <Link
                    href={{
                      pathname: router.pathname,
                      query: {
                        products_category: getCategoryString(item.name_slug, currentRegionObj?.nameSlug),
                        page: '1'
                      }
                    }}
                  >
                    <a>{item.name}</a>
                  </Link>
                </MenuItem>
              )
            })}
          </Menu>
        </Sider>
        <Layout>
          <Header style={{ paddingLeft: 20 }}>
            <h1>
              {t['seo.sitemap.product_h1']
                .replace('{category}', currentCategoryObj?.name || '')
                .replace('{regionName}', currentRegionObj?.name || '')}
            </h1>
          </Header>
          <Layout style={{ padding: '0 24px' }}>
            <div>
              <Breadcrumb style={{ margin: '16px 0' }}>
                {breadcrumbs.map((item, index) => {
                  return (
                    <Breadcrumb.Item href={item.href} key={index}>
                      {item.name}
                    </Breadcrumb.Item>
                  )
                })}
              </Breadcrumb>
            </div>
            <Content>
              <RegionList
                onClick={handleReginClick}
                currentCategoryNameSlug={currentCategoryNameSlug}
                currentRegionObj={currentRegionObj}
              />
              <List loading={loading} className={styles.listWrap} style={{ marginBottom: '20px' }}>
                {dataList.map((item, index) => {
                  return <ProductItem item={item} key={index} />
                })}
                {!loading && dataList.length > 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Pagination
                      className={styles.pageWrap}
                      total={pageObj.total}
                      pageSize={pageObj.pageSize}
                      current={currentUrlPage ? +currentUrlPage : 1}
                      itemRender={(page: number, type: 'page' | 'more' | 'prev' | 'next', originElement: ReactNode) => {
                        if (type === 'page') {
                          return (
                            <Link
                              href={{
                                pathname: router.pathname,
                                query: {
                                  products_category: getCategoryString(
                                    currentCategoryNameSlug,
                                    currentRegionObj?.nameSlug
                                  ),
                                  page: `${page}`
                                }
                              }}
                            >
                              <a>{page}</a>
                            </Link>
                          )
                        }
                        return originElement
                      }}
                    />
                  </div>
                ) : null}
              </List>
              <FooterLinks
                category={currentCategoryObj?.name || ''}
                region={currentRegionObj?.name || ''}
                useFooterLinks={useFooterLinks}
              />
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </div>
  )
}

export default ProductSitemap
